/**
 * Twin process-capability resource — typed wrapper over `POST /v1/twin/capability`
 * (Six Sigma DMAIC report, task #368).
 *
 * The server computes Cp/Cpk/Pp/Ppk + sigma level (continuous CTQs) and DPMO/DPU/sigma
 * (discrete) over the granularity-bucketed heats/lots — the numbers that make a report
 * "Six Sigma". Spec limits (USL/LSL/target/opportunities) are passed per-CTQ from the report's
 * capture and fall back to the param's bound-tag min/max. POST (not GET) because the spec map
 * is structured.
 */

import { api } from "@/lib/api-client";

/** Per-CTQ spec input — any subset; a one-sided spec drops the missing side. */
export interface CapabilitySpecInput {
  usl?: number | null;
  lsl?: number | null;
  target?: number | null;
  opportunities?: number | null;
}

export interface CapabilityRequest {
  granularity?: "heat" | "lot" | "day" | "week" | "month";
  from?: string;
  to?: string;
  /** param → spec limits. Use the reserved key "__overall__" to set discrete opportunities/unit. */
  specs?: Record<string, CapabilitySpecInput>;
}

/** One continuous CTQ's capability result. Indices are null when their spec side is absent or the
 *  sample is insufficient. */
export interface CapabilityContinuous {
  param: string;
  label: string;
  n: number;
  mean: number;
  sigmaST: number;
  sigmaLT: number;
  cp: number | null;
  cpk: number | null;
  pp: number | null;
  ppk: number | null;
  sigmaLevelLT: number | null;
  sigmaLevelST: number | null;
  yield: number | null;
  usl: number | null;
  lsl: number | null;
  target: number | null;
  /** Authority class of the spec limits (S3.1a fork-D4). Only "customer" (+ a calibrated gauge)
   *  may back a Cpk; a display-only/internal band is withheld to %-out-of-band. "tag" is legacy. */
  specSource: "customer" | "internal" | "display_only" | "captured" | "tag" | "none";
  /** Fraction (0–1) of observations outside [lsl,usl] — the honest metric shown when a Cpk is
   *  withheld. Present whenever a limit exists; null with no limits. */
  pctOutOfBand: number | null;
  /** Non-empty when the capability indices are withheld (the gate reason + how to unlock it):
   *  a display/internal band, a carry-forward series, or an unsupported sample. "" when emitted. */
  withheld?: string;
  insufficient: boolean;
}

/** The org's discrete (attribute) sigma over the window. */
export interface CapabilityDiscrete {
  units: number;
  defects: number;
  opportunities: number;
  dpu: number;
  dpmo: number;
  yieldFT: number;
  sigmaLevel: number | null;
  note?: string;
}

export interface CapabilityAnalysis {
  granularity: "heat" | "lot" | "day" | "week" | "month";
  nObservations: number;
  continuous: CapabilityContinuous[];
  discrete: CapabilityDiscrete;
  sigmaShift: number;
}

export const twinCapability = {
  compute: (body?: CapabilityRequest) =>
    api.post<CapabilityAnalysis>("/v1/twin/capability", body ?? {}),
};
